import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Reusable confirmation dialog hook to replace `window.confirm`.
 *
 * Why this exists: browsers — especially mobile Chrome — silently disable
 * `window.confirm()` after a few uses on the same page (anti-spam feature).
 * When that happens the prompt never shows, the handler sees `false`, and
 * the user thinks the button is broken. This dialog runs in React state so
 * it's immune to that throttling and styled to match the app.
 *
 * Usage:
 *   const { confirm, dialog } = useConfirmDialog();
 *   const onDelete = async () => {
 *     const ok = await confirm({ title: "Delete user?", message: "...", confirmText: "Delete", variant: "destructive" });
 *     if (!ok) return;
 *     await api.delete(...);
 *   };
 *   return <>{dialog}<Button onClick={onDelete} /></>;
 */
export const useConfirmDialog = () => {
  const [state, setState] = useState({
    open: false,
    title: "",
    message: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    variant: "default", // "default" | "destructive"
  });
  const resolverRef = useRef(null);

  // Resolve to `false` if the component unmounts mid-prompt.
  useEffect(() => () => {
    if (resolverRef.current) {
      try { resolverRef.current(false); } catch { /* noop */ }
      resolverRef.current = null;
    }
  }, []);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState((prev) => ({
        ...prev,
        open: true,
        title: opts.title || "Are you sure?",
        message: opts.message || "",
        confirmText: opts.confirmText || "Confirm",
        cancelText: opts.cancelText || "Cancel",
        variant: opts.variant || "default",
      }));
    });
  }, []);

  const handleOpenChange = (next) => {
    if (!next && resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }
    setState((prev) => ({ ...prev, open: next }));
  };

  const handleConfirm = () => {
    if (resolverRef.current) {
      resolverRef.current(true);
      resolverRef.current = null;
    }
    setState((prev) => ({ ...prev, open: false }));
  };

  const dialog = (
    <AlertDialog open={state.open} onOpenChange={handleOpenChange}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{state.title}</AlertDialogTitle>
          {state.message && (
            <AlertDialogDescription className="whitespace-pre-line">
              {state.message}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-dialog-cancel">
            {state.cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-dialog-confirm"
            onClick={handleConfirm}
            className={
              state.variant === "destructive"
                ? "bg-red-600 hover:bg-red-700 text-white"
                : ""
            }
          >
            {state.confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirm, dialog };
};

export default useConfirmDialog;
